import { Result, err, ok } from "neverthrow";
import countries from "i18n-iso-countries";

import { logError } from "common/debug-print";
import { stripeClient } from "webapp/lib/stripe-api";
import { inputTextSize } from "webapp/ui/smart/buy-screen/buy-screen-style";
import { systemFontFamily } from "common/BaseTheme";
import { BuilderTheme } from "sharedUI/styles";
import { CardInfo } from "./payment-info";
import { getAppFacilities } from "webapp/lib/app-renderer";

// eslint-disable-next-line @typescript-eslint/no-var-requires
countries.registerLocale(require("i18n-iso-countries/langs/en.json"));
const COUNTRY_CODES = countries.getAlpha2Codes();

export async function createStripeCard(theme: BuilderTheme): Promise<any> {
    // REVIEW: Singleton reference, I think, need to verify. Was this reviewed?
    const stripeSDK = await stripeClient();

    // Stripe expects us to use non-computed CSS for Elements.
    // Thankfully! We get to set CSS here, too.
    const card = stripeSDK.elements().create("card", {
        style: {
            base: {
                fontSize: inputTextSize,
                fontFamily: systemFontFamily,
                color: theme.fgColorDark,
                ":-webkit-autofill": {
                    fontSize: inputTextSize,
                    fontFamily: systemFontFamily,
                    color: theme.fgColorDark,
                },
            },
        },
        hidePostalCode: true,
    });

    return card;
}

export function cardIsValid(cardInfo: CardInfo, stripeCard: any, stripeCardReady: boolean) {
    const { nameOnCard, add1, city, state, country, zip, receiptEmail } = cardInfo;
    const hasEmptyRequired = [nameOnCard, add1, city, state, country, zip, receiptEmail].some(s => s === "");
    const countryParses = Object.keys(COUNTRY_CODES).includes(country);
    const cardValid = stripeCard !== undefined && stripeCardReady && countryParses && !hasEmptyRequired;
    return cardValid;
}

export async function getPaymentSetupIntent(ownerID: string): Promise<Result<string, string>> {
    // FIXME: Some code duplication with `user-header.tsx`
    const setupResponse = await getAppFacilities().callAuthCloudFunction("updateUnifiedPaymentInformation", {
        ownerID,
    });
    if (setupResponse === undefined || setupResponse.status !== 402) {
        return err("Something went wrong updating payment information");
    }
    const setupIntent = (await setupResponse.json()).setupIntentClientSecret;
    if (typeof setupIntent !== "string") return err("Something went wrong updating payment information");
    return ok(setupIntent);
}

export async function setNewDefaultPaymentMethod(
    ownerID: string,
    paymentMethodID: string
): Promise<Result<undefined, string>> {
    const attachResponse = await getAppFacilities().callAuthCloudFunction("updateUnifiedPaymentInformation", {
        ownerID,
        paymentMethodID,
    });
    if (attachResponse === undefined || !attachResponse.ok) {
        return err("Something went wrong updating payment information");
    }
    return ok(undefined);
}

export async function getPaymentMethodForStripeCard(cardInfo: CardInfo, stripeCard: any) {
    const { nameOnCard, receiptEmail, add1, add2, city, state, country, zip } = cardInfo;

    if (stripeCard === undefined) return err("stripe card not found");

    // REVIEW: Singleton reference, I think, need to verify
    const stripeSDK = await stripeClient();

    try {
        const paymentInfo = await stripeSDK.createPaymentMethod("card", stripeCard, {
            billing_details: {
                name: nameOnCard,
                email: receiptEmail,
                address: {
                    city,
                    country,
                    line1: add1,
                    line2: add2,
                    state,
                    postal_code: zip,
                },
            },
        });
        if (paymentInfo.error !== undefined) {
            logError("Creating Stripe paymentMethod", paymentInfo.error);
            return err(paymentInfo.error.message ?? "Error creating stripe paymentMethod");
        }

        return ok(paymentInfo.paymentMethod?.id);
    } catch (e: unknown) {
        logError("When handling Stripe payment information", e);
        return err("Something went wrong getting your payment method information");
    }
}

export async function confirmCardSetup(
    setupIntentClientSecret: string,
    paymentMethodId: string | undefined
): Promise<Result<string, string>> {
    try {
        const stripeSDK = await stripeClient();

        const setupIntent = await stripeSDK.confirmCardSetup(setupIntentClientSecret, {
            payment_method: paymentMethodId,
        });
        if (setupIntent.error !== undefined) {
            logError("Attaching Stripe paymentMethod to setupIntent", setupIntent.error);
            return err(setupIntent.error.message ?? "Error attaching Stripe paymentMethod to setupIntent");
        }

        const setupIntentPaymentMethod = setupIntent.setupIntent?.payment_method as string;
        return ok(setupIntentPaymentMethod);
    } catch (e: unknown) {
        logError("When handling Stripe payment information", e);
        return err("Something went wrong updating your payment information");
    }
}
